import { Button } from "@/components/ui/button";
import { CheckCircle, Users, Award, Globe } from "lucide-react";

const highlights = [
  {
    icon: Users,
    title: "Expert Team",
    description: "Certified professionals with deep expertise across cloud, security, and enterprise architecture.",
  },
  {
    icon: Award,
    title: "Proven Results",
    description: "98% client retention rate with a track record of successful digital transformation projects.",
  },
  {
    icon: Globe,
    title: "Global Reach",
    description: "Serving clients across North America, Europe, and APAC with localized support.",
  },
];

const points = [
  "ISO 27001 & SOC 2 Type II certified",
  "Multi-cloud expertise (AWS, Azure, GCP)",
  "Dedicated account management",
  "Flexible engagement models",
];

export const About = () => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="about" className="py-24 px-4 bg-white dark:bg-slate-900">
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                alt="Our Team"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            </div>

            {/* Floating stat */}
            <div className="absolute -bottom-6 -right-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-5 min-w-[160px]">
              <div className="text-3xl font-bold text-sky-500">15+</div>
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">Years delivering<br />IT excellence</div>
            </div>

            {/* Floating stat 2 */}
            <div className="absolute -top-6 -left-6 bg-sky-500 rounded-2xl shadow-xl shadow-sky-500/30 p-5 min-w-[140px]">
              <div className="text-3xl font-bold text-white">500+</div>
              <div className="text-sm text-sky-100 mt-1">Enterprise<br />clients served</div>
            </div>

            {/* Glow */}
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
          </div>

          {/* Content side */}
          <div className="space-y-8 order-1 lg:order-2">
            <span className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20">
              About Us
            </span>
            <h2 className="text-3xl md:text-5xl font-bold leading-tight text-slate-900 dark:text-white">
              Driving Digital Innovation
              <span className="bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent">
                {" "}With Purpose
              </span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              With over 15 years of experience, Luxe-Tech has helped hundreds of businesses achieve
              their digital transformation goals. Our certified professionals bring deep expertise
              across cloud, security, and enterprise architecture to deliver solutions tailored to
              your unique challenges.
            </p>

            {/* Checkpoints */}
            <ul className="grid grid-cols-2 gap-3">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle className="h-4 w-4 text-sky-500 flex-shrink-0 mt-0.5" />
                  {p}
                </li>
              ))}
            </ul>

            {/* Highlights */}
            <div className="space-y-4">
              {highlights.map((h) => {
                const Icon = h.icon;
                return (
                  <div key={h.title} className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-sky-100 dark:bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">{h.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{h.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              size="lg"
              onClick={() => scrollTo("contact")}
              className="bg-sky-500 hover:bg-sky-600 text-white border-0 rounded-full px-8 h-12 shadow-lg shadow-sky-500/20 hover:-translate-y-0.5 transition-all"
            >
              Work With Us
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
